import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { firebaseConfig, vapidKey } from "./firebase-config";
import { enableNativePush } from "./native-push";
import { api } from "./api";

export type PushStatus = "granted" | "denied" | "default" | "unsupported";

export function pushPermission(): PushStatus {
  if (Capacitor.isNativePlatform()) return "default";
  if (typeof window === "undefined" || !("Notification" in window) || !("serviceWorker" in navigator)) {
    return "unsupported";
  }
  return Notification.permission as PushStatus;
}

let registeredToken: string | null = null;

export async function enablePush(): Promise<PushStatus> {
  if (Capacitor.isNativePlatform()) return enableNativePush();
  if (pushPermission() === "unsupported") return "unsupported";
  if (!(await isSupported().catch(() => false))) return "unsupported";

  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm as PushStatus;

  const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  const swConfig = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  });
  const swUrl = `${import.meta.env.BASE_URL}firebase-messaging-sw.js?${swConfig.toString()}`;
  const reg = await navigator.serviceWorker.register(swUrl, { scope: import.meta.env.BASE_URL });
  await navigator.serviceWorker.ready;
  const messaging = getMessaging(app);
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: reg });
  if (token && token !== registeredToken) {
    await api.registerPushToken(token, "web");
    registeredToken = token;
  }
  onMessage(messaging, () => {});
  return "granted";
}

export async function autoPromptPush(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await enableNativePush();
    return;
  }
  const p = pushPermission();
  if (p === "unsupported" || p === "denied") return;
  try {
    await enablePush();
  } catch (e) {
    console.warn("auto push prompt failed", e);
  }
}

export async function resumePush(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await enableNativePush();
    return;
  }
  if (pushPermission() !== "granted") return;
  try {
    await enablePush();
  } catch (e) {
    console.warn("push registration failed", e);
  }
}

export async function disablePush(): Promise<void> {
  if (!registeredToken) return;
  try {
    await api.deletePushToken(registeredToken);
  } finally {
    registeredToken = null;
  }
}
